import * as z from 'zod'

import { affiliateCouponTagSchema } from '@/domain/coupons'
import { REPORT_GRANULARITIES } from '@/utils/report-period'

// TODO(MKT-832): hand-authored while the campaign-stats endpoint is unreleased.
// Regenerate the affiliates Orval client once gto-wizard#2825 reaches dev and
// drop this module in favour of the generated models.

/**
 * Money arrives as a JSON string from Django's Decimal, but a hand-rolled
 * fixture or a future serializer change can send a number — accept both and
 * let `toAmount` narrow at the render site, as the coupon schemas do.
 */
const decimalLike = z.union([z.number(), z.string()])

/**
 * Why redemptions did or did not become billable orders. Counts REDEMPTIONS, so
 * it need not sum to `codes_converted`, which collapses a multi-use code to 1.
 */
export const affiliateCampaignRedemptionOutcomesSchema = z.object({
  counted: z.number(),
  free: z.number(),
  refunded: z.number(),
  unattributed: z.number(),
  awaiting: z.number(),
})

export type AffiliateCampaignRedemptionOutcomesSchema = z.infer<
  typeof affiliateCampaignRedemptionOutcomesSchema
>

export const affiliateCampaignRowSchema = z.object({
  tag: affiliateCouponTagSchema.nullable(),
  codes_minted: z.number(),
  codes_unused: z.number(),
  codes_attempted: z.number(),
  codes_converted: z.number(),
  codes_abandoned: z.number(),
  codes_in_flight: z.number(),
  orders: z.number(),
  unique_customers: z.number(),
  revenue_usd: decimalLike,
  commission_usd: decimalLike,
  net_usd: decimalLike,
  // Optional, not defaulted: this ships ahead of the backend, and an absent
  // breakdown must stay distinguishable from a genuine all-zero one so the
  // explainer hides rather than claiming every bucket is empty.
  redemption_outcomes: affiliateCampaignRedemptionOutcomesSchema.optional(),
})

export type AffiliateCampaignRowSchema = z.infer<typeof affiliateCampaignRowSchema>

export const affiliateCampaignSeriesRowSchema = z.object({
  period_label: z.string(),
  period_start: z.string(),
  orders: z.number(),
  conversions: z.number(),
  revenue_usd: decimalLike,
  commission_usd: decimalLike,
})

export type AffiliateCampaignSeriesRowSchema = z.infer<typeof affiliateCampaignSeriesRowSchema>

export const affiliateCampaignStatsResponseSchema = z.object({
  date_from: z.string().nullable(),
  date_to: z.string().nullable(),
  granularity: z.enum(REPORT_GRANULARITIES),
  rows: z.array(affiliateCampaignRowSchema),
  totals: affiliateCampaignRowSchema,
  overlapping_code_count: z.number(),
  series: z.array(affiliateCampaignSeriesRowSchema).nullable(),
  partner_tier_missing: z.boolean().optional(),
})

export type AffiliateCampaignStatsResponseSchema = z.infer<
  typeof affiliateCampaignStatsResponseSchema
>

/**
 * Row identity for the comparison grid. The untagged bucket has no tag id, and
 * a tag name is not unique enough to key on, so it gets a reserved sentinel.
 */
export const UNTAGGED_ROW_ID = 'untagged'

export const campaignRowId = (row: AffiliateCampaignRowSchema): string =>
  row.tag?.id ?? UNTAGGED_ROW_ID

/**
 * A ÷ B, or null when B is zero. The shared "no denominator is not zero"
 * guard behind every derived campaign figure — see `funnelRatePct` for the
 * percent form and `avgOrderValue` for the money form.
 */
export const safeRatio = (numerator: number, denominator: number): number | null =>
  denominator > 0 ? numerator / denominator : null

/**
 * Share of a funnel step that reached the next one, as a whole-number percent.
 * A zero denominator is "nothing to convert", not an error — so it yields null
 * and the caller renders a dash rather than NaN% or a misleading 0%.
 */
export const funnelRatePct = (numerator: number, denominator: number): number | null => {
  const ratio = safeRatio(numerator, denominator)

  return ratio === null ? null : ratio * 100
}

/**
 * Mean revenue per order. Distinguishes a campaign that drew a few large
 * subscriptions from one that drew many cheap ones — two campaigns can post
 * identical revenue and be worth very different amounts of partner attention.
 */
export const avgOrderValue = (revenueUsd: number, orders: number): number | null =>
  safeRatio(revenueUsd, orders)

/**
 * Codes somebody has tried to redeem at least once, all-time.
 *
 * `codes_unused` and `codes_minted` are both all-time snapshots, so their
 * difference is the one partition of the minted pool that stays sound with a
 * date filter applied — unlike `codes_attempted` / `codes_converted`, which are
 * scoped to the selected range and therefore cannot be segments of the same
 * whole. Clamped at zero so BE drift renders a degenerate bar, never a
 * negative width.
 */
export const codesTouched = (row: AffiliateCampaignRowSchema): number =>
  Math.max(0, row.codes_minted - row.codes_unused)

/**
 * Running revenue total across the series, oldest → newest. A campaign has a
 * start and an end, so "how much have we banked so far" is often the more
 * legible question than the per-bucket sawtooth.
 */
export const cumulate = (values: number[]): number[] => {
  let running = 0

  return values.map((value) => {
    running += value

    return running
  })
}

/**
 * Redemptions that produced no billable order, by reason.
 *
 * The funnel counts redemptions synchronously at checkout while the money tiles
 * count commissions written by a later async task, so "converted" standing
 * against $0 is expected in several situations and alarming in none of the
 * obvious ones. Returns the reasons that actually apply, so the caller can stay
 * silent when every redemption was billed.
 */
export const unbilledRedemptions = (
  outcomes: AffiliateCampaignRedemptionOutcomesSchema | undefined,
): { id: 'free' | 'refunded' | 'unattributed' | 'awaiting'; count: number }[] => {
  if (outcomes === undefined) {
    return []
  }

  return (['free', 'refunded', 'unattributed', 'awaiting'] as const)
    .map((id) => ({ id, count: outcomes[id] }))
    .filter(({ count }) => count > 0)
}

/**
 * A campaign with no codes and no orders has nothing to chart; the caller shows
 * an empty state instead of axes of zeros. Reservations alone still count as
 * activity — a campaign mid-checkout is not empty.
 */
export const isCampaignEmpty = (row: AffiliateCampaignRowSchema): boolean =>
  row.codes_minted === 0 && row.codes_attempted === 0 && row.orders === 0
